
import { useState } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { BadeelLogo } from "@/components/shared/badeel-logo";
import { BadeelFullLogo } from "@/components/shared/badeel-full-logo";
import { Eye, EyeOff, Phone } from "lucide-react";

export default function Login() {
  const [, setLocation] = useLocation();
  const { t } = useTranslation();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!phone || !password) {
      toast({
        title: t("validationError") || "Validation Error",
        description: t("phoneAndPasswordRequired") || "رقم الهاتف وكلمة المرور مطلوبان",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      console.log('🔐 Starting login process...', { phone });
      const response = await login(phone, password);
      
      console.log('🔐 Login response received:', response);
      
      if (response && response.success) {
        console.log('🔐 Login successful, redirecting...');
        toast({
          title: t("loginSuccessful") || "Login Successful",
          description: t("welcomeBack") || "Welcome back!",
        });
        // Don't clear form data - just redirect
        setLocation("/dashboard");
      } else {
        throw new Error(response?.message || 'Login failed');
      }
    } catch (error: any) {
      console.error("🔐 Login error:", error);
      
      // Extract error message
      let errorMessage = t("invalidCredentials") || "بيانات اعتماد غير صحيحة";
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      toast({
        title: t("loginFailed") || "Login Failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      // Don't clear password for security, but keep phone
      // Password will be cleared by user if needed
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhoneInput = (value: string) => {
    // Remove all non-digit characters except +
    let digits = value.replace(/[^\d+]/g, '');
    
    // If it doesn't start with +, add it
    if (!digits.startsWith('+')) {
      digits = '+' + digits.replace(/^\+/, '');
    }
    
    // Format as Saudi phone number
    if (digits.startsWith('+966')) {
      return digits;
    } else if (digits.startsWith('+5') && digits.length <= 12) {
      return '+966' + digits.substring(1);
    } else if (digits.startsWith('+0') && digits.length <= 13) {
      return '+966' + digits.substring(2);
    } else if (digits.startsWith('+') && digits.length > 1) {
      const nums = digits.substring(1);
      if (nums.startsWith('966')) {
        return digits;
      } else if (nums.startsWith('5') && nums.length <= 9) {
        return '+966' + nums;
      }
    }
    
    return digits;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhone(formatPhoneInput(value));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <BadeelFullLogo width={200} height={120} className="max-w-full h-auto" />
          </div>
          <h1 className="text-3xl font-bold text-primary">
            {t("appName") || "Emaar Platform"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            E-commerce Management Dashboard
          </p>
        </div>

        {/* Login Card */}
        <Card className="shadow-xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-semibold text-center">
              {t("login") || "Login"}
            </CardTitle>
            <p className="text-sm text-muted-foreground text-center">
              Enter your credentials to access your account
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Phone Number Field */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">
                  {t("phoneNumber") || "Phone Number"}
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="+966501234567"
                    required
                    className="pl-10 h-11"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  {t("password") || "Password"}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("enterPassword") || "Enter your password"}
                    required
                    className="pr-10 h-11"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Login Button */}
              <Button 
                type="submit" 
                className="w-full h-11 text-base font-medium" 
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t("signingIn") || "Signing In..."}
                  </>
                ) : (
                  t("signIn") || "Sign In"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-muted-foreground">
            © 2024 Emaar Platform. {t("allRightsReserved") || "All rights reserved."}
          </p>
        </div>
      </div>
    </div>
  );
}
